import { useMemo, type CSSProperties } from "react";
import { SkyDataGrid, SkyText, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import type { EntityProgressRow } from "@shared/types/runs";
import {
  ENTITY_PAGE_SIZE,
  ENTITY_PAGE_SIZE_OPTIONS,
  ENTITY_STAGE_FILTER_OPTIONS,
  entityStagePresentation,
  formatCount,
  notReadYet,
  withEntityStage,
  type EntityStageRow,
} from "@/components/runs/runFormatting";
import { StageBadge } from "@/components/runs/StageBadge";
import { StatusCell } from "@/components/runs/StatusCell";
import { useReportingConnections } from "@/components/runs/useReportingConnections";
import { pixelColumnLayout } from "@/lib/grid";

/**
 * One connection's tables in one run: what was extracted, what was loaded, and where each table
 * got to (Session 7, O1; Session 10, B17).
 *
 * Shared by the connection's last-run card and the expanded rows of its Runs tab, so a table
 * reads the same in both places. The caller filters the run's entity rows to the connection;
 * this grid never decides whose rows they are.
 */

export interface RunTablesGridProps {
  /** The run's entity rows for ONE connection. */
  rows: EntityProgressRow[];
  /** Whether the run is over: a table still waiting reads differently in a finished run. */
  terminal: boolean;
  /** True when the run's load said it had no destination (`loadHadNoDestination`). */
  noDestination?: boolean;
  /** #1651: connections whose load waits for an import slot (`loadsWaitingForSlot`). */
  waitingLoads?: ReadonlySet<string>;
  /** #1672: connections whose load is sending the tables' files (`loadsSendingFiles`). */
  sendingLoads?: ReadonlySet<string>;
  /** #2389: the run the rows belong to, so what the grid holds starts again with a new run. */
  runId?: string;
  testId?: string;
}

/**
 * The Extracted cell. A dash when no extract count stands behind the row: only the load reported
 * it, or the table has not started (`pending`, or a planned `waiting` table the extract has not
 * reached, #1127), the same as the run page's grid. "0" would say it was read and had no rows.
 * #2389: so is a table that reads "Waiting to read" (`notReadYet`).
 */
function extractedText(row: EntityProgressRow, reportingRows: ReadonlySet<string>): string {
  if (row.extractReported === false) return "—";
  if (notReadYet(row, reportingRows)) return "—";
  if (row.records === 0 && (row.status === "waiting" || row.status === "pending")) return "—";
  return formatCount(row.records);
}

export function RunTablesGrid({
  rows,
  terminal,
  noDestination = false,
  waitingLoads,
  sendingLoads,
  runId,
  testId,
}: RunTablesGridProps) {
  // #2389: the connections whose connector reports rows read as it goes ("Waiting to read"),
  // held for the rest of the run.
  const reportingRows = useReportingConnections(rows, runId);
  const data = useMemo(
    () => withEntityStage(rows, terminal, noDestination, waitingLoads, sendingLoads, reportingRows),
    [rows, terminal, noDestination, waitingLoads, sendingLoads, reportingRows],
  );
  const columns: SkyDataGridColumn<EntityStageRow>[] = [
    {
      key: "entityName",
      header: "Table",
      width: "10%",
      renderCell: (_value, row) => (
        <SkyText size={200} weight="semibold" block truncate>
          {row.entityName}
        </SkyText>
      ),
    },
    {
      key: "records",
      header: "Extracted",
      width: "10%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {extractedText(row, reportingRows)}
        </SkyText>
      ),
    },
    {
      key: "loaded",
      header: "Loaded",
      width: "10%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.load?.status === "skipped" && !row.load.noRows
            ? "Not loaded"
            : formatCount(row.load?.rowsLoaded)}
        </SkyText>
      ),
    },
    {
      key: "stage",
      header: "Status",
      width: "10%",
      // #1465: the stage the badge shows, over every page.
      filterable: true,
      filterConfig: { type: "select", options: [...ENTITY_STAGE_FILTER_OPTIONS] },
      renderCell: (_value, row) => {
        const stage = entityStagePresentation(
          row,
          terminal,
          noDestination,
          waitingLoads,
          sendingLoads,
          reportingRows,
        );
        // The badge at its own width: a bare badge in a grid cell draws as a bar (StatusCell).
        return (
          <StatusCell
            testId={`${testId ?? "run-tables"}-status-${row.entityName}`}
            badge={<StageBadge presentation={stage} />}
          />
        );
      },
    },
  ];

  // The status badge and the wrapped headers get a measured floor, so a narrow panel scrolls
  // sideways instead of squeezing the badge into its neighbour. The widest badge is #2392's "No
  // rows yet, table not created" (30 characters, 9 more than "Extracted, not loaded").
  const layout = pixelColumnLayout(columns, {
    minimums: { entityName: 120, records: 104, loaded: 96, stage: 218 },
    selection: false,
  });
  return (
    <div style={{ "--run-tables-floor": `${layout.floor}px` } as CSSProperties}>
      <SkyDataGrid<EntityStageRow>
        data={data}
        columns={layout.columns}
        getRowId={(row) => row.entityName}
        bordered
        pagination
        pageSize={ENTITY_PAGE_SIZE}
        pageSizeOptions={ENTITY_PAGE_SIZE_OPTIONS}
        className="[&_table]:min-w-(--run-tables-floor) [&_th]:whitespace-normal"
        {...(testId ? { testId } : {})}
      />
    </div>
  );
}
